import React, { useState, useEffect, forwardRef } from "react";
import { useDispatch } from "react-redux";
import { Draggable, Droppable, DragDropContext } from "react-beautiful-dnd";
import { DateTime } from "luxon";
import { startCase } from 'lodash'
import { getUUID } from "../../../utils/hashFuncs";
import validators from "../../../utils/forms/validators";
import FormModal from "../../theme/modals/FormModal";
import TemplateMergeModal from "./TemplateMergeModal";
import { mergeTemplateToApp } from "../../../store/calls/applicationData";
import { toggleGlobalLoader } from "../../../store/actions/session";
import { addSuccessAlert } from "../../../store/calls/alertLoadingDispatch";




const getIconElement = type => {

    switch (type) {
        case 'file':
            return <i className="fas fa-file" />

        case 'question':
            return <i className="fas fa-question-circle" />

        case 'alert':
            return <i className="fas fa-message" />

        default:
            return <i className="fab fa-elementor" />

    }
}

function DraggableApplicationElement(props) {

    // render function for boxes that represent inputs on the form flow
    const renderInputDisplayBody = () => {

        return (
            <>
                <h4 className="is-size-5 has-text-primary">
                    {getIconElement(props.type)}
                    <span className="ml-4">{props.name}</span>
                </h4>
                <p>
                    {props.comments || <span className="has-text-grey-light">--</span>}
                    {props.input_type && <span className="mx-2 has-text-grey-light is-size-7">({startCase(props.input_type)})</span>}
                    {props.required &&
                        <span className="mx-2 is-italic has-text-grey-light is-size-7">Required</span>
                    }
                </p>
            </>
        )

    }
    // render function for boxes that represent questions on the form flow
    const renderMsgDisplayBody = () => {
        return (
            <>
                <h4 className="is-size-5 has-text-gray-dark">
                    <i className="fas fa-comment-dots"/>
                    <span className="ml-4">User Message ({props.alertType})</span>
                </h4>
                <p>
                    {props.msg}
                </p>
            </>
        )

    }

    return (
        <Draggable draggableId={props.id} index={props.index}>
            {(provided, snapshot) => (
                <div
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    {...provided.dragHandleProps}
                >
                    <div className="p-4 my-4 box">
                        <div className="is-flex is-justify-content-space-between">
                            <div className="mr-4">
                                {props.input_type == 'alert' ? renderMsgDisplayBody() : renderInputDisplayBody()}
                            </div>

                            
                            <div className="is-flex-shrink-0">
                                <a
                                    className="p-2"
                                    onClick={e => {
                                        e.preventDefault()
                                        props.onEdit()
                                    }}
                                >
                                    <i
                                        className="fas fa-edit has-text-primary"
                                    />
                                </a>
                                <a
                                    className="p-2"
                                    onClick={e => {
                                        e.preventDefault()
                                        props.onDelete()
                                    }}
                                >
                                    <i
                                        className="fas fa-trash-alt has-text-danger"
                                    />
                                </a>

                            </div>
                        </div>
                    </div>
                </div>
            )}
        </Draggable>
    )
}



function AddEditApplicationElementModal({
    onSubmit,
    onCancel,
    editingElement,
    type
}) {

    let [element, setElement] = useState(editingElement || {});

    let [msgMode, setMsgMode] = useState(editingElement?.input_type === 'alert');


    // default inputs for all questions
    const startingInputs = [
        {
            type: 'text',
            label: "Name",
            dataKey: "name",
            ...validators.isStringOfMinLength(2)
        },
        {
            type: 'text',
            label: "Comments",
            dataKey: "comments",
        },
        {
            type: 'select',
            label: "Question Type",
            dataKey: "input_type",
            inputClasses: "is-fullwidth",
            options: [
                {
                    display: "Text (Default)",
                    value: 'text'
                },
                {
                    display: "Long Text",
                    value: 'long_text'
                },
                {
                    display: "Yes/No",
                    value: "yes_no"
                },
                {
                    display: "Date",
                    value: "date"
                },
                {
                    display: "Dollars",
                    value: "dollars"
                },
                {
                    display: "Number",
                    value: "number"
                }
            ]
        },
        {
            type: 'checkbox',
            label: "Required",
            dataKey: "required",
            defaultChecked: true

        },
    ]

    const [inputs, setInputs] = useState(startingInputs)

    useEffect(() => {

        // messages have different inputs, short circuit the update
        if (msgMode) {
            setInputs([
                {
                    type: "select",
                    label: "Type",
                    subLabel: `This colors the box and changes the icon. Info is the site's standard text color. 
                             Warning adds a bright yellow background. Error adds a bright red background.`,
                    options: [{ "display": "Info", "value": "info" }, { "display": "Warning", "value": "warning" }, { "display": "Danger", "value": "danger" }],
                    dataKey: "alertType",
                    className: "is-fullwidth"
                },
                {
                    type: "textarea",
                    label: "Message",
                    subLabel: "The message that will be displayed to the user as text",
                    dataKey: "msg",
                },
                {
                    dataKey: "input_type",
                    value: "alert",
                    inputClasses: "is-hidden"
                }
            ].map(d => (editingElement && editingElement[d.dataKey]) ? { ...d, value: editingElement[d.dataKey] } : d))

            return;
        }


        setInputs(
            startingInputs
                .filter(d => {
                    // filter out question type for doc and other element creates
                    if (type === 'file') {
                        return d.dataKey !== 'input_type'
                    }
                    return true

                })
                .map(d => ({
                    ...d,
                    value: element[d.dataKey],
                    onChange: val => setElement({ ...element, [d.dataKey]: val })
                }))

        )


        if (type === 'qualifier') {
            setInputs(startingInputs.map(d => d.dataKey === 'input_type' ?
                {
                    ...d,
                    value: 'yes_no',
                    options: [{
                        display: "Yes/No",
                        value: "yes_no"
                    }]
                } : {
                    ...d,
                    value: element[d.dataKey] || d.value,
                    onChange: val => setElement({ ...element, [d.dataKey]: val })
                }))
        }

    }, [msgMode, type, editingElement])


    return <FormModal
        title={element.id ? `Edit Application ${startCase(type)}` : `Add Application ${startCase(type)}`}
        visible
        inputs={inputs}
        disableAutocomplete
        onSubmit={onSubmit}
        onCancel={onCancel}
        otherButtons={[
            // don't show if we are editing an element, because you can't change from question to message types
            (editingElement?.type ? null : <div
                className="button is-primary is-outlined"
                onClick={() =>{
                    setMsgMode(!msgMode);
                }} 
            >
                Change to adding a {msgMode ? startCase(type) : "message"}
            </div>)
        ]}
    />

}


function DragDropApplicationSection({
    elements,
    type,
    onAddElement,
    onEditElement,
    onRemoveElement,
    onMoveElement
}) {

    let [displayCreateEditModal, setDisplayCreateEditModal] = useState(false);
    let [editingElement, setEditingElement] = useState(null);

    const onDragEnd = result => {
        const { destination, source, draggableId } = result;
        onMoveElement(source.index, destination.index);
    }

    const onSubmitCreateEditForm = data => {
        if (editingElement?.id) {

            console.log(editingElement, data, { ...editingElement, ...data })
            onEditElement({ ...editingElement, ...data })
        } else {
            onAddElement({
                ...data,
                id: getUUID(),
                type
            })
        }
        setDisplayCreateEditModal(false)
        setEditingElement(null);
    }

    const onCancelCreateEditForm = () => {
        setEditingElement(null);
        setDisplayCreateEditModal(false);
    }

    return (
        <div>
            <h4 className="is-size-4">{startCase(type)}s</h4>
            <DragDropContext
                onDragEnd={onDragEnd}
            >
                <Droppable droppableId={`droppable-${type}}`} type={type}>
                    {(provided, snapshot) => (
                        <div
                            ref={provided.innerRef}

                            {...provided.droppableProps}
                        >
                            {(elements || []).map((d, i) => {
                                /*
                                * add a conditional here to only render if type matches
                                * We cannot use a filter, because the indexes must match the index of the parent
                                * array for dragdrop to work, as all updates handled by parent.
                                * */
                                if (d.type === type) {
                                    return <DraggableApplicationElement
                                        key={d.id}
                                        {...d}
                                        index={i}
                                        onDelete={() => onRemoveElement(d)}
                                        onEdit={() => {
                                            setEditingElement(d);
                                            setDisplayCreateEditModal(true);
                                        }}
                                    />
                                }
                            })}

                            {provided.placeholder}
                        </div>
                    )}
                </Droppable>
            </DragDropContext>
            {/* addition element */}
            <div className="columns">
                <div className="column">
                    <div className="p-4 my-4 box cursor-is-pointer " onClick={() => setDisplayCreateEditModal(true)}>
                        <h4 className="is-size-5 has-text-link">
                            <i className="fas fa-plus-circle" />
                            <span className="ml-4">Add an element</span>
                        </h4>
                    </div>
                </div>
            </div>

            {displayCreateEditModal && <AddEditApplicationElementModal
                onSubmit={onSubmitCreateEditForm}
                onCancel={onCancelCreateEditForm}
                editingElement={editingElement}
                type={type}
            />}

        </div>
    )
}


function ApplicationBuilder({ 
    children, 
    template = {},
    onSave,
    showMergeOptions = false,
    mergeIntoObject,
    isGlobalTemplate = false
}) {

    const [elements, setElements] = useState(template.elements || []);
    const [mergeModalVisible, setMergeModalVisible] = useState(false);

    useEffect(() => {
        setElements(template.elements || [])
    }, [template])


    const actions = {
        onAddElement: (element) => {
            console.log("ELEMENT CHECK", element)
            setElements([...elements, {
                ...element
            }])
        },

        onEditElement: (element) => {
            let idx = elements.findIndex(d => d.id === element.id);
            if (idx < 0) {
                console.error(`Could not find element with id ${element.id} for updating.`)
                return
            }
            const newElements = [...elements];
            newElements[idx] = { ...newElements[idx], ...element };
            setElements(newElements);
        },

        onRemoveElement: (element) => {
            setElements(elements.filter(d => d.id !== element.id))
        },

        onMoveElement: (source_idx, destination_idx) => {
            const newElements = [...elements]
            const movedElement = newElements.splice(source_idx, 1)[0]
            newElements.splice(destination_idx, 0, movedElement)
            setElements(newElements)
        }

    }

    const onTemplateMerge = template_id => {
        mergeTemplateToApp(mergeIntoObject.id, template_id)
            .then(data => setElements(data.template.elements))
    }

    return (
        <>
            <div
                className="section"
                style={{ maxWidth: '900px' }}
            >
                {children}
                <div className="buttons mx-4 is-justify-content-flex-start">
                    <button
                        className="button is-link"
                        onClick={() => {
                            Promise.resolve(onSave({ template: { ...template, elements } }))
                            // .then(() => setNeedsSaving(false))
                        }}
                    >
                        <i className="fas fa-save mr-3" />
                        Save Changes
                    </button>
                    {!!showMergeOptions && (
                        <button
                            className="button"
                            onClick={() => {
                                setMergeModalVisible(true);
                            }}
                        >
                            Merge with another template
                        </button>

                    )}
                </div>

                <div className="p-4 pt-0">
                    <DragDropApplicationSection
                        elements={elements}
                        type="qualifier"
                        {...actions}
                    />
                </div>

                <div className="p-4 pt-0">
                    <DragDropApplicationSection
                        elements={elements}
                        type="question"
                        {...actions}
                    />
                </div>
                <div className="p-4">
                    <DragDropApplicationSection
                        elements={elements}
                        type="file"
                        {...actions}
                    />
                </div>
                {/* <div className="p-4"> */}
                {/*    <div> */}
                {/*        <h4 className="is-size-4">Configuration</h4> */}
                {/*        <div className="p-4 my-4 box cursor-is-pointer " */}
                {/*             onClick={() => { */}
                {/*                 let loan_elem = elements.find(d => d.type === 'loans') */}
                {/*                 if (loan_elem) { */}
                {/*                     actions.onRemoveElement(loan_elem) */}
                {/*                 } else { */}
                {/*                      actions.onAddElement({ */}
                {/*                          id: 'loans', */}
                {/*                          type: 'loans', */}
                {/*                          name: 'Loans' */}
                {/*                      }) */}
                {/*                 } */}
                {/*             }}> */}
                {/*            <h4 className="is-size-5"> */}
                {/*                {(elements || []).find(d => d.type === 'loans') ? <i className="fas fa-check-square has-text-link"/> : <i className="far fa-square"/>} */}
                {/*                <span className="ml-4">Show Loan Section</span> */}
                {/*            </h4> */}
                {/*        </div> */}
                {/*    </div> */}
                {/* </div> */}
            </div>


            <TemplateMergeModal
                visible={mergeModalVisible}
                onClose={() => setMergeModalVisible(false)}
                onSelect={(template_id) => {
                    toggleGlobalLoader(true)
                    setMergeModalVisible(false);
                    mergeTemplateToApp(mergeIntoObject.id, template_id, isGlobalTemplate)
                        .then((data) => {
                            setElements(data.template?.elements)
                            toggleGlobalLoader(false);
                            addSuccessAlert("Template Merged")
                        })
                }}
            />
        </>
    )


}


export default ApplicationBuilder;